import { useEffect, useState } from "react";

import { useReducedMotion } from "./use-reduced-motion";

/**
 * MS57 — alive-mascot verb-rotation hook.
 *
 * Ported from catering's Sprint 196 hook of the same name. Lucky
 * confirmed in MS57 that we want the same pattern on the Lembra
 * celebrations platform — sage Sparkles + rotating display-italic
 * verb + 3-dot wave — wherever the LLM is "thinking" (the chat
 * composer between user-message and assistant-reply, the Cmd-K
 * Ask AI surface, the landing SmartIntentBar parse moment).
 *
 * The verb names the actual pipeline step the LLM is on right now
 * ("Reading the brief", "Matching vendors", "Picking the why").
 * Each verb crossfades into the next every ~2.5s via a keyed
 * remount: the caller uses `index` as React `key` on the rendered
 * element, which re-mounts on every swap and replays the
 * `.verb-fade` keyframe defined in src/index.css.
 *
 * When `active` is false (no thinking happening), the hook idles
 * at index 0 and stops the interval. Useful for surfaces that
 * render the strip conditionally but want a stable starting
 * frame the next time they activate.
 *
 * MS177: respects prefers-reduced-motion — when the user has the
 * OS-level reduce-motion preference set, the hook pins to verb 0
 * and never rotates. The caller's surrounding affordance (the
 * Sparkles glyph + 3-dot wave + the verb text itself) still
 * signals "thinking" without the motion.
 *
 * Inspiration: Anthropic's flower + verb-rotation pattern, Notion
 * AI's "Noodling" cycle. We keep the rhythm, drop the cuteness —
 * Lembra's Aesop-archetype brand voice rejects "Noodling".
 */
interface UseRotatingVerbOptions {
  verbs: readonly string[];
  active?: boolean;
  /** ms between verb swaps. Default 2500 — slow enough to read as
   *  deliberate craft, fast enough that a 10s wait shows progress. */
  intervalMs?: number;
}

export function useRotatingVerb({
  verbs,
  active = true,
  intervalMs = 2500,
}: UseRotatingVerbOptions): { verb: string; index: number } {
  const [index, setIndex] = useState(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!active || verbs.length <= 1 || reducedMotion) {
      setIndex(0);
      return;
    }
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % verbs.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [active, intervalMs, verbs.length, reducedMotion]);

  return { verb: verbs[index] ?? verbs[0] ?? "", index };
}
